/**
 * AddReviewCommentDialog Component
 *
 * Dialog for manually adding review comments from permit reviewers.
 * Features:
 * - Smart categorization based on comment text
 * - Source detection from content
 * - Review cycle tracking
 * - Priority and due date settings
 *
 * Part of Phase 3: Submission Guidance & Review Tracking
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, MessageSquare, Sparkles } from 'lucide-react';
import {
  createDefaultComment,
  categorizeComment,
  detectSource,
  parseReviewCycle,
} from '@/hooks/useReviewComments';
import type {
  ReviewComment,
  ReviewCommentSource,
  ReviewCommentCategory,
} from '@/types/jurisdiction';

interface AddReviewCommentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (comment: Omit<ReviewComment, 'id' | 'jobId' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  editingComment?: ReviewComment;
  currentReviewCycle?: number;
}

const sourceOptions: Array<{ value: ReviewCommentSource; label: string }> = [
  { value: 'building_dept', label: 'Building Department' },
  { value: 'fire_marshal', label: 'Fire Marshal' },
  { value: 'zoning', label: 'Zoning' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'environmental', label: 'Environmental' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'hoa', label: 'HOA' },
  { value: 'other', label: 'Other' },
];

const categoryOptions: Array<{ value: ReviewCommentCategory; label: string }> = [
  { value: 'plans_correction', label: 'Plans Correction' },
  { value: 'missing_document', label: 'Missing Document' },
  { value: 'code_violation', label: 'Code Violation' },
  { value: 'clarification_needed', label: 'Clarification Needed' },
  { value: 'fee_issue', label: 'Fee Issue' },
  { value: 'site_issue', label: 'Site Issue' },
  { value: 'approval_condition', label: 'Approval Condition' },
  { value: 'other', label: 'Other' },
];

const priorityOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

export function AddReviewCommentDialog({
  open,
  onOpenChange,
  onSave,
  editingComment,
  currentReviewCycle = 1,
}: AddReviewCommentDialogProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [showSmartSuggestions, setShowSmartSuggestions] = useState(false);

  // Form state
  const [originalText, setOriginalText] = useState('');
  const [source, setSource] = useState<ReviewCommentSource>('building_dept');
  const [sourceName, setSourceName] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [category, setCategory] = useState<ReviewCommentCategory>('plans_correction');
  const [reviewCycle, setReviewCycle] = useState(currentReviewCycle);
  const [pageReference, setPageReference] = useState('');
  const [sheetReference, setSheetReference] = useState('');
  const [priority, setPriority] = useState<string>('');
  const [dueDate, setDueDate] = useState('');

  // Smart suggestions based on text analysis
  const [suggestedSource, setSuggestedSource] = useState<ReviewCommentSource | null>(null);
  const [suggestedCategory, setSuggestedCategory] = useState<ReviewCommentCategory | null>(null);

  // Reset form when dialog opens/closes or when editing changes
  useEffect(() => {
    if (open) {
      if (editingComment) {
        setOriginalText(editingComment.originalText);
        setSource(editingComment.source);
        setSourceName(editingComment.sourceName || '');
        setReviewerName(editingComment.reviewerName || '');
        setCategory(editingComment.category);
        setReviewCycle(editingComment.reviewCycle);
        setPageReference(editingComment.pageReference || '');
        setSheetReference(editingComment.sheetReference || '');
        setPriority(editingComment.priority || '');
        setDueDate(editingComment.dueDate || '');
        setShowSmartSuggestions(false);
      } else {
        setOriginalText('');
        setSource('building_dept');
        setSourceName('');
        setReviewerName('');
        setCategory('plans_correction');
        setReviewCycle(currentReviewCycle);
        setPageReference('');
        setSheetReference('');
        setPriority('');
        setDueDate('');
        setShowSmartSuggestions(true);
      }
      setSuggestedSource(null);
      setSuggestedCategory(null);
    }
  }, [open, editingComment, currentReviewCycle]);

  // Analyze text for smart suggestions
  useEffect(() => {
    if (originalText.length > 10 && showSmartSuggestions) {
      const detectedSource = detectSource(originalText);
      const detectedCategory = categorizeComment(originalText);

      if (detectedSource !== source) {
        setSuggestedSource(detectedSource);
      } else {
        setSuggestedSource(null);
      }

      if (detectedCategory !== category) {
        setSuggestedCategory(detectedCategory);
      } else {
        setSuggestedCategory(null);
      }
    } else {
      setSuggestedSource(null);
      setSuggestedCategory(null);
    }
  }, [originalText, source, category, showSmartSuggestions]);

  const handleSave = async () => {
    if (!originalText.trim()) return;

    setIsSaving(true);

    try {
      const comment = createDefaultComment({
        originalText: originalText.trim(),
        source,
        sourceName: sourceName.trim() || undefined,
        reviewerName: reviewerName.trim() || undefined,
        category,
        reviewCycle,
        pageReference: pageReference.trim() || undefined,
        sheetReference: sheetReference.trim() || undefined,
        priority: priority as 'low' | 'medium' | 'high' | 'critical' | undefined,
        dueDate: dueDate || undefined,
        receivedAt: editingComment?.receivedAt || new Date().toISOString(),
        status: editingComment?.status || 'open',
      });

      await onSave(comment);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save comment:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const applySuggestion = (type: 'source' | 'category') => {
    if (type === 'source' && suggestedSource) {
      setSource(suggestedSource);
      setSuggestedSource(null);
    } else if (type === 'category' && suggestedCategory) {
      setCategory(suggestedCategory);
      setSuggestedCategory(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            {editingComment ? 'Edit Review Comment' : 'Add Review Comment'}
          </DialogTitle>
          <DialogDescription>
            {editingComment
              ? 'Update the review comment details.'
              : 'Enter a comment from the permit reviewer. Smart categorization will help organize it.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Comment Text */}
          <div className="space-y-2">
            <Label htmlFor="comment-text">
              Comment Text <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="comment-text"
              value={originalText}
              onChange={(e) => setOriginalText(e.target.value)}
              placeholder="Paste or type the reviewer's comment here..."
              rows={4}
              disabled={isSaving}
            />
          </div>

          {/* Smart Suggestions */}
          {(suggestedSource || suggestedCategory) && (
            <div className="p-3 bg-primary/5 rounded-lg space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Sparkles className="h-4 w-4 text-primary" />
                Smart Suggestions
              </div>
              <div className="flex flex-wrap gap-2">
                {suggestedSource && (
                  <Badge
                    variant="outline"
                    className="cursor-pointer hover:bg-primary/10"
                    onClick={() => applySuggestion('source')}
                  >
                    Source: {sourceOptions.find((s) => s.value === suggestedSource)?.label}
                    <span className="ml-1 text-xs text-muted-foreground">(click to apply)</span>
                  </Badge>
                )}
                {suggestedCategory && (
                  <Badge
                    variant="outline"
                    className="cursor-pointer hover:bg-primary/10"
                    onClick={() => applySuggestion('category')}
                  >
                    Category: {categoryOptions.find((c) => c.value === suggestedCategory)?.label}
                    <span className="ml-1 text-xs text-muted-foreground">(click to apply)</span>
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Source and Category */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="source">Source Department</Label>
              <Select value={source} onValueChange={(v) => setSource(v as ReviewCommentSource)}>
                <SelectTrigger id="source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sourceOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as ReviewCommentCategory)}>
                <SelectTrigger id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Reviewer Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="source-name">Department Name (optional)</Label>
              <Input
                id="source-name"
                value={sourceName}
                onChange={(e) => setSourceName(e.target.value)}
                placeholder="e.g., Miami-Dade Building"
                disabled={isSaving}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reviewer-name">Reviewer Name (optional)</Label>
              <Input
                id="reviewer-name"
                value={reviewerName}
                onChange={(e) => setReviewerName(e.target.value)}
                placeholder="e.g., John Smith"
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Review Cycle and References */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="review-cycle">Review Cycle</Label>
              <Select
                value={reviewCycle.toString()}
                onValueChange={(v) => setReviewCycle(parseInt(v, 10))}
              >
                <SelectTrigger id="review-cycle">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map((cycle) => (
                    <SelectItem key={cycle} value={cycle.toString()}>
                      {cycle === 1 ? '1st Review' : cycle === 2 ? '2nd Review' : `${cycle}${cycle === 3 ? 'rd' : 'th'} Review`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="page-ref">Page Reference</Label>
              <Input
                id="page-ref"
                value={pageReference}
                onChange={(e) => setPageReference(e.target.value)}
                placeholder="e.g., Page 3"
                disabled={isSaving}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sheet-ref">Sheet Reference</Label>
              <Input
                id="sheet-ref"
                value={sheetReference}
                onChange={(e) => setSheetReference(e.target.value)}
                placeholder="e.g., E-101"
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Priority and Due Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="priority">Priority (optional)</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger id="priority">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {priorityOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="due-date">Due Date (optional)</Label>
              <Input
                id="due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                disabled={isSaving}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !originalText.trim()}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : editingComment ? (
              'Save Changes'
            ) : (
              'Add Comment'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddReviewCommentDialog;
